import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
    DEFAULT_MOCK_SETTINGS,
    FormPageMock,
    type MockSettingsT,
} from "@/features/developer/form-anatomy/FormPageMock";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

export const Route = createFileRoute("/_app/design/forms/")({
    component: FormsShowcasePage,
});

const FORM_DESIGN_LINKS = [
    {
        to: "/design/forms/pages",
        title: "Pages",
        description: "Cover, instructions, question and block screens, with every variable labelled.",
    },
    {
        to: "/design/forms/inputs",
        title: "Inputs",
        description: "Every input type with an example label, caption, description and error.",
    },
    {
        to: "/design/forms/blocks",
        title: "Blocks",
        description: "Text styles, images, videos and image carousels shown between questions.",
    },
    {
        to: "/design/forms/entry-values",
        title: "Entry values",
        description: "How each answer reads back once saved, with codes and own options shown as their labels.",
    },
] as const;

function Setting({ label, column, children }: { label: string; column: string; children: ReactNode }) {
    return (
        <div className="flex justify-between items-center gap-4 py-2.5 border-slate-200 border-b">
            <div className="min-w-0">
                <p className="font-medium text-sm">{label}</p>
                <code className="text-muted-foreground text-xs">{column}</code>
            </div>
            {children}
        </div>
    );
}

function Choice<T extends string>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
}) {
    return (
        <ui.Tabs value={value} onValueChange={(next) => onChange(next as T)}>
            <ui.TabsList>
                {options.map((option) => (
                    <ui.TabsTrigger key={option.value} value={option.value}>
                        {option.label}
                    </ui.TabsTrigger>
                ))}
            </ui.TabsList>
        </ui.Tabs>
    );
}

function FormsShowcasePage() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Forms", route: { to: "/design/forms" } },
    ]);
    const [settings, setSettings] = useState<MockSettingsT>(DEFAULT_MOCK_SETTINGS);
    const update = (patch: Partial<MockSettingsT>) => setSettings((prev) => ({ ...prev, ...patch }));

    return (
        <AdminPageLayout
            title="Forms"
            description="What patients see on a form. Change a form setting to see the cover and a question page update."
            maxWidthClassName="max-w-6xl"
        >
            <div className="space-y-10">
                <div className="items-start gap-8 grid grid-cols-1 lg:grid-cols-[18rem_1fr]">
                    <section aria-labelledby="form-settings">
                        <h2 id="form-settings" className="mb-1 font-semibold text-sm">
                            Form settings
                        </h2>
                        <Setting label="Side" column="trackers.side">
                            <Choice
                                value={settings.side ?? "none"}
                                options={[
                                    { value: "none", label: "None" },
                                    { value: "Left", label: "Left" },
                                    { value: "Right", label: "Right" },
                                ]}
                                onChange={(side) => update({ side: side === "none" ? null : side })}
                            />
                        </Setting>
                        <Setting label="Title alignment" column="forms.title_align">
                            <Choice
                                value={settings.titleAlign}
                                options={[
                                    { value: "left", label: "Left" },
                                    { value: "center", label: "Centre" },
                                ]}
                                onChange={(titleAlign) => update({ titleAlign })}
                            />
                        </Setting>
                        <Setting label="Time and question count" column="forms.show_cover_metrics">
                            <ui.Switch
                                checked={settings.showCoverMetrics}
                                onCheckedChange={(showCoverMetrics) => update({ showCoverMetrics })}
                                aria-label="Show time and question count"
                            />
                        </Setting>
                        <Setting label="Optional marker" column="forms.show_required_marker">
                            <ui.Switch
                                checked={settings.showRequiredMarker}
                                onCheckedChange={(showRequiredMarker) => update({ showRequiredMarker })}
                                aria-label="Show optional marker"
                            />
                        </Setting>
                        <Setting label="Schedule line" column="schedule step label">
                            <ui.Switch
                                checked={settings.showSchedule}
                                onCheckedChange={(showSchedule) => update({ showSchedule })}
                                aria-label="Show schedule line"
                            />
                        </Setting>
                        <ui.Button variant="link" className="px-0" onClick={() => setSettings(DEFAULT_MOCK_SETTINGS)}>
                            Reset settings
                        </ui.Button>
                    </section>
                    <FormPageMock settings={settings} />
                </div>

                <section className="space-y-3">
                    <h2 className="font-semibold text-lg">More detail</h2>
                    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                        {FORM_DESIGN_LINKS.map((link) => (
                            <Link key={link.to} to={link.to} className="block">
                                <ui.Card className="hover:border-sky-300 h-full transition-colors">
                                    <ui.CardHeader>
                                        <ui.CardTitle>{link.title}</ui.CardTitle>
                                        <ui.CardDescription>{link.description}</ui.CardDescription>
                                    </ui.CardHeader>
                                </ui.Card>
                            </Link>
                        ))}
                    </div>
                </section>
            </div>
        </AdminPageLayout>
    );
}
